import React, { ReactNode, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import {
  Origin,
  ReferenceSource,
  ReferenceType,
} from '@1po/1po-bff-fe-spec/generated/catalog/trading_data/request/GetReferencesStockRequest';
import { CatalogSource } from '@1po/1po-bff-fe-spec/generated/common/vehicle/CatalogSource';
import { matchRoute, ROUTER_PRODUCT } from 'app/AppRouter';
import { RootState } from 'app/AppStore';
import { trackAppEvent } from 'app/AppTracker';
import { QuantityBox } from 'components/QuantityBox/QuantityBox';
import QuantityModule from 'components/QuantityModule';
import { QuantityModuleUpdateComponentType } from 'components/QuantityModule/QuantityModule';
import PriceButtonsModule from 'components/ReferencePriceSection/PriceButtonsModule';
import { ProductQuantityWrapper } from 'components/ReferencePriceSection/ReferencePriceSection.styled';
import { ButtonSize } from 'components/ReferencePriceSection/ReferencePriceSectionUtils';
import {
  getOilChunk,
  getTechnicalCharacteristicsValue,
  isBulkCertasOil,
  isCertasOil,
  isNonBulkCertasOil,
} from 'components/StockInfo/catalogStockUtils';
import PrivateComponent from 'composers/PrivateComponent';
import { useIsBulkOilUser, useIsOilChunkUserR1 } from 'domains/appContext/AppContext.requests';
import {
  getBasketOtherSection,
  getBasketVehicles,
  updateReferenceQuantity,
  updateReferenceQuantityRequest,
} from 'domains/basket/Basket.store';
import {
  CERTAS_OIL_REFERENCE_QUANTITY_LIMIT,
  REFERENCE_MAX_QUANTITY_LIMIT,
  ReferenceLocal as BasketReferenceLocal,
} from 'domains/basket/Basket.types';
import { removeCartReferenceInCatalog } from 'domains/catalog/Catalog.store';
import { CERTAS_OIL_TECHNICAL_CHARACTERISTICS_CHUNK_CODE } from 'domains/catalog/Catalog.types';
import { DHReferenceLocal, getDHReference, getStockInfo, hasMultipleOffers } from 'domains/references';
import { UserRole } from 'domains/user';
import { ButtonShape, Flex, MarginBox, notifyTop } from 'UI';
import { hasData, NO_DATA } from 'utils';
import {
  TRACKING_EVENT_CART_PART_QUANTITY_ZERO,
  TRACKING_EVENT_PAGE_PRODUCT_PART_DELETION,
  TRACKING_EVENT_PAGE_RESULT_PART_DELETION,
} from 'utils/eventTracker/EventTracker.types';
import { getMaxOrderQuantityForFamily, isSingleUnitOrderRestrictedFamily } from 'utils/rights';

const resolveIsBulkChunkReference = (
  isBulkOilUser: boolean,
  bulkChunkReferences: BasketReferenceLocal[] | undefined,
  referenceNumber: string,
  isBulkOilReference: boolean,
): boolean =>
  isBulkOilUser && (bulkChunkReferences?.some((ref) => ref.referenceNumber === referenceNumber) || isBulkOilReference);

const resolveIsCertasOilReference = (
  isBulkOilUser: boolean,
  isOilUser: boolean,
  resolvedFamilyCode?: string,
  resolvedProductModelCode?: string,
  resolvedPackaging?: string,
): boolean =>
  (isBulkOilUser && isBulkCertasOil(resolvedFamilyCode, resolvedProductModelCode, resolvedPackaging)) ||
  (isOilUser && isCertasOil(resolvedFamilyCode, resolvedProductModelCode));

const getOverflowMessage = (
  isSingleUnitRestrictedReference: boolean,
  t: (key: string, defaultValue: string) => string,
): string =>
  isSingleUnitRestrictedReference
    ? t(
        'cart.action.add_reference.traction_battery_single_unit_tooltip',
        'This part is restricted to single-unit orders.',
      )
    : t(
        'catalog.reference.stock.notification.parts_unavailable',
        'Sorry, Looks like the desired number of parts is currently unavailable.',
      );

const getZeroQuantityTrackingEvent = (type: QuantityModuleUpdateComponentType, isOnProductPage: boolean) => {
  if (type === 'INPUT') {
    return TRACKING_EVENT_CART_PART_QUANTITY_ZERO;
  }
  return isOnProductPage ? TRACKING_EVENT_PAGE_PRODUCT_PART_DELETION : TRACKING_EVENT_PAGE_RESULT_PART_DELETION;
};

const resolveDhReferenceData = (
  dhReference: DHReferenceLocal | NO_DATA,
  dhReferenceFallback: DHReferenceLocal | NO_DATA,
): DHReferenceLocal | undefined => {
  const dhReferenceFallbackData = hasData(dhReferenceFallback) ? dhReferenceFallback : undefined;
  return hasData(dhReference) ? dhReference : dhReferenceFallbackData;
};

const resolveBasketReferences = (
  vehicleKey: string | undefined,
  isApplicableToCurrentVehicle: boolean,
  referencesOfCurrentVeh: BasketReferenceLocal[] | undefined,
  otherSectionReferences: BasketReferenceLocal[] | undefined,
): BasketReferenceLocal[] | undefined =>
  vehicleKey && isApplicableToCurrentVehicle ? referencesOfCurrentVeh : otherSectionReferences;

interface ResolveChunkParams {
  isOilUser: boolean;
  chunkReferences: BasketReferenceLocal[] | undefined;
  referenceNumber: string;
  currentBasketReference: BasketReferenceLocal | undefined;
  dhChunk: string | undefined;
  resolvedFamilyCode?: string;
  resolvedProductModelCode?: string;
  resolvedPackaging?: string;
}

const resolveChunk = ({
  isOilUser,
  chunkReferences,
  referenceNumber,
  currentBasketReference,
  dhChunk,
  resolvedFamilyCode,
  resolvedProductModelCode,
  resolvedPackaging,
}: ResolveChunkParams): number => {
  if (!isOilUser) {
    return 1;
  }
  // Try existing chunkReferences first (basket refs with familyCode populated)
  const chunkRef = chunkReferences?.find((ref) => ref.referenceNumber === referenceNumber);
  if (chunkRef) {
    return getOilChunk(chunkRef, dhChunk);
  }
  // Fallback: use resolved DH reference data for current reference identification
  if (isNonBulkCertasOil(resolvedFamilyCode, resolvedProductModelCode, resolvedPackaging)) {
    return getOilChunk(currentBasketReference, dhChunk);
  }
  return 1;
};

interface OrderActionsProps {
  referenceNumber: string;
  referenceType: ReferenceType;
  catalogSource: CatalogSource;
  handleAddToCartClick: (sellerName?: string, sellerNumber?: string) => void;
  vehicleKey?: string;
  isApplicableToCurrentVehicle?: boolean;
  availableForOrder?: boolean;
  isExadis?: boolean;
  isAvailableSoon?: boolean;
  isNoLongerAvailable?: boolean;
  enabled?: boolean;
  origin?: Origin;
  supplierCode?: string;
  referenceSource?: ReferenceSource;
  withIcon?: boolean;
  showFirstHelp?: boolean;
  useCompactView?: boolean;
  externalButton?: ReactNode;
  buttonSize?: ButtonSize;
  buttonShape?: ButtonShape;
  handleClickCompare?: (reference: string) => void;
  handleMultipleOffersButtonClick?: React.Dispatch<React.SetStateAction<boolean>>;
  isMKTP?: boolean;
  sellerNumber?: string;
  sellerName?: string;
  stretchCartButton?: boolean;
  centerQuantityModule?: boolean;
}

const OrderActionsSection = ({
  referenceNumber,
  referenceType,
  catalogSource,
  handleAddToCartClick,
  vehicleKey,
  isApplicableToCurrentVehicle = false,
  availableForOrder = true,
  isExadis = false,
  isAvailableSoon,
  isNoLongerAvailable,
  enabled = true,
  origin,
  supplierCode,
  referenceSource,
  withIcon = false,
  showFirstHelp,
  useCompactView = false,
  externalButton,
  buttonSize = 'middle',
  buttonShape = 'default',
  handleClickCompare,
  handleMultipleOffersButtonClick,
  isMKTP = false,
  sellerNumber,
  sellerName,
  stretchCartButton = false,
  centerQuantityModule = false,
}: OrderActionsProps) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const vehicles = useSelector(getBasketVehicles);
  const location = useLocation();
  const otherSection = useSelector(getBasketOtherSection);
  const stock = useSelector((state: RootState) => getStockInfo(state, referenceNumber, sellerNumber));
  const maxQuantity = stock?.data?.maxConfirmedQuantity;
  const referenceWithMultipleOffers = useSelector((state: RootState) => hasMultipleOffers(state, referenceNumber));
  const dhReference = useSelector((state: RootState) => getDHReference(state, { vehicleKey, referenceNumber }));
  const dhReferenceFallback = useSelector((state: RootState) =>
    getDHReference(state, { vehicleKey: undefined, referenceNumber }),
  );
  const dhReferenceData = resolveDhReferenceData(dhReference, dhReferenceFallback);
  const dhChunk = getTechnicalCharacteristicsValue(dhReferenceData, CERTAS_OIL_TECHNICAL_CHARACTERISTICS_CHUNK_CODE);
  const isOilUser = useIsOilChunkUserR1();
  const isBulkOilUser = useIsBulkOilUser();
  const referencesOfCurrentVeh = useMemo(
    () => vehicles.find((vehicle) => vehicle.vehicleDetail.vehicleKey === vehicleKey)?.references,
    [vehicleKey, vehicles],
  );

  const isInVehicleReferences = referencesOfCurrentVeh?.some(
    (ref) => ref.referenceNumber === referenceNumber && ref.sellerNumber === sellerNumber,
  );
  const basketReferences = resolveBasketReferences(
    vehicleKey,
    isApplicableToCurrentVehicle,
    referencesOfCurrentVeh,
    otherSection?.references,
  );
  const currentBasketReference = basketReferences?.find(
    (ref) => ref.referenceNumber === referenceNumber && ref.sellerNumber === sellerNumber,
  );

  // Resolve Certas oil fields from basket reference with DH reference fallback
  const resolvedFamilyCode = currentBasketReference?.familyCode ?? dhReferenceData?.familyCode;
  const resolvedProductModelCode = currentBasketReference?.productModelCode ?? dhReferenceData?.productModelCode;
  const resolvedPackaging = currentBasketReference?.packaging ?? dhReferenceData?.packaging;

  const isBulkOilReference = isBulkCertasOil(resolvedFamilyCode, resolvedProductModelCode, resolvedPackaging);
  const isSingleUnitRestrictedReference = isSingleUnitOrderRestrictedFamily(resolvedFamilyCode);
  const isCertasOilForMaxQty = isCertasOil(resolvedFamilyCode, resolvedProductModelCode);
  const certasOilMaxQuantity =
    isBulkOilReference || isOilUser ? CERTAS_OIL_REFERENCE_QUANTITY_LIMIT : REFERENCE_MAX_QUANTITY_LIMIT;
  const maxQuantityValue = getMaxOrderQuantityForFamily(
    resolvedFamilyCode,
    isCertasOilForMaxQty ? certasOilMaxQuantity : maxQuantity,
  );
  const chunkReferences = basketReferences?.filter((ref) =>
    isNonBulkCertasOil(ref.familyCode, ref.productModelCode, ref.packaging),
  );
  const bulkChunkReferences = basketReferences?.filter((ref) =>
    isBulkCertasOil(ref.familyCode, ref.productModelCode, ref.packaging),
  );
  const isBulkChunkReference = resolveIsBulkChunkReference(
    isBulkOilUser,
    bulkChunkReferences,
    referenceNumber,
    isBulkOilReference,
  );
  const isCertasOilReference = resolveIsCertasOilReference(
    isBulkOilUser,
    isOilUser,
    resolvedFamilyCode,
    resolvedProductModelCode,
    resolvedPackaging,
  );
  const refQuantity = currentBasketReference?.quantity as number;
  const getChunk = () =>
    resolveChunk({
      isOilUser,
      chunkReferences,
      referenceNumber,
      currentBasketReference,
      dhChunk,
      resolvedFamilyCode,
      resolvedProductModelCode,
      resolvedPackaging,
    });

  const isTire = referenceType === ('TIRE' as ReferenceType);

  const useVehicleBasket = !!vehicleKey && isApplicableToCurrentVehicle && isInVehicleReferences;

  const quantityCallback = useCallback(
    (callbackQuantity: number | string, componentType?: QuantityModuleUpdateComponentType) => {
      const quantity = typeof callbackQuantity === 'string' ? parseInt(callbackQuantity, 10) : callbackQuantity;
      const type: QuantityModuleUpdateComponentType = componentType ?? 'INPUT';

      if (isNaN(quantity)) {
        return;
      }

      if (quantity === 0) {
        dispatch(
          removeCartReferenceInCatalog({
            vehicleKey: useVehicleBasket ? vehicleKey : undefined,
            basketReferenceType: useVehicleBasket ? 'VEHICLE' : 'OTHER',
            reference: { referenceNumber, sellerNumber },
            referenceType,
            origin,
            supplierCode,
            referenceSource: referenceSource ?? 'STANDARD',
          }),
        );
        trackAppEvent(getZeroQuantityTrackingEvent(type, matchRoute(location.pathname, ROUTER_PRODUCT)));
      } else {
        dispatch(
          updateReferenceQuantityRequest({
            basketReferenceType: useVehicleBasket ? 'VEHICLE' : 'OTHER',
            vehicleKey: useVehicleBasket ? vehicleKey : undefined,
            externalBasketId: undefined,
            newQuantity: quantity,
            referenceToUpdate: { referenceNumber, sellerNumber },
            origin,
            supplierCode,
            referenceSource,
            referenceType,
            isBulkOilReference,
          }),
        );
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dispatch, useVehicleBasket, location.pathname, referenceNumber, vehicleKey],
  );

  const onMaxValueOverflow = useCallback(() => {
    if (stock?.data?.maxConfirmedQuantity !== undefined) {
      dispatch(
        updateReferenceQuantity({
          vehicleKey,
          referenceToUpdate: { referenceNumber, sellerNumber },
          newQuantity: stock?.data?.maxConfirmedQuantity,
          externalBasketId: undefined,
        }),
      );
    }
    const overflowMessage = getOverflowMessage(isSingleUnitRestrictedReference, t);

    notifyTop('info', overflowMessage);
  }, [
    dispatch,
    isSingleUnitRestrictedReference,
    referenceNumber,
    stock?.data?.maxConfirmedQuantity,
    t,
    vehicleKey,
    sellerNumber,
  ]);

  if (!availableForOrder) {
    return null;
  }

  return (
    <Flex direction="row" align={'flex-end'}>
      {currentBasketReference && (!referenceWithMultipleOffers || sellerNumber) ? (
        <ProductQuantityWrapper
          align="center"
          gap={40}
          justify={centerQuantityModule ? 'center' : undefined}
          $centered={centerQuantityModule}
          data-cy={'order-actions-quantity-wrapper'}
        >
          {externalButton}
          {useCompactView && isTire ? (
            <Flex minWidth={250} justify={'center'}>
              <MarginBox ml={70}>
                <QuantityBox
                  medium
                  value={refQuantity}
                  onChange={quantityCallback}
                  maxValue={maxQuantity}
                  isCompact={useCompactView}
                />
              </MarginBox>
            </Flex>
          ) : (
            <QuantityModule
              value={refQuantity}
              onChange={quantityCallback}
              showDelete
              maxValue={maxQuantityValue}
              onMaxValueOverflow={onMaxValueOverflow}
              isSingleUnitRestricted={isSingleUnitRestrictedReference}
              chunk={getChunk()}
              isMinBulkChunk={isBulkChunkReference}
              size={externalButton ? 'middle' : buttonSize}
              isCertasOilReference={isCertasOilReference}
            />
          )}
        </ProductQuantityWrapper>
      ) : (
        <PrivateComponent
          requiredRights={[UserRole.COMMAND, UserRole.CONNECT_COMMANDE]}
          render={() => (
            <PriceButtonsModule
              referenceNumber={referenceNumber}
              handleAddToCartClick={handleAddToCartClick}
              handleClickCompare={handleClickCompare}
              catalogSource={catalogSource}
              stock={stock}
              isExadis={isExadis}
              isAvailableSoon={isAvailableSoon}
              isNoLongerAvailable={isNoLongerAvailable}
              enabled={enabled}
              withIcon={withIcon}
              showFirstHelp={showFirstHelp}
              useCompactView={useCompactView}
              externalButton={externalButton}
              buttonSize={buttonSize}
              buttonShape={buttonShape}
              handleMultipleOffersButtonClick={handleMultipleOffersButtonClick}
              isMKTP={isMKTP}
              sellerNumber={sellerNumber}
              sellerName={sellerName}
              referenceWithMultipleOffers={referenceWithMultipleOffers}
              stretchCartButton={stretchCartButton}
            />
          )}
        />
      )}
    </Flex>
  );
};

export default OrderActionsSection;
